import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Flex,
  Spinner,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
} from "@chakra-ui/react";
import { supabase } from "../../../lib/supabaseClient";

const PAGE_SIZE = 20;
const columns = [
  { key: "isrc", label: "ISRC" },
  { key: "name_album", label: "앨범명" },
  { key: "name_musician", label: "뮤지션명" },
  { key: "name_track", label: "트랙명" },
  { key: "commission_rate", label: "수수료율" },
  { key: "ownership_user", label: "오너쉽 유저" },
  { key: "ownership_share", label: "지분" },
  { key: "created_date", label: "등록일" },
  { key: "actions", label: "관리" },
];

const ShortsAlbums = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const toast = useToast();
  const [inputPage, setInputPage] = useState(page.toString());

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: rows, error } = await supabase.rpc(
        "get_ownership_with_commission",
        {
          search: search || null,
          page: parseInt(page),
        }
      );

      if (error) throw error;

      setData(rows || []);
      if (rows && rows.length > 0) {
        setTotalPages(Math.max(1, Math.ceil(rows[0].total_count / PAGE_SIZE)));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "데이터를 불러오는데 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [search, page]);

  useEffect(() => {
    setInputPage(String(page));
  }, [page]);

  const handleGrant = async () => {
    if (!selected || !emailInput) return;

    try {
      // 1. 사용자 존재 여부 확인
      const { data: userData, error: userError } = await supabase
        .from("user")
        .select("id")
        .eq("email", emailInput)
        .single();

      if (userError || !userData) {
        toast({
          title: "사용자를 찾을 수 없습니다.",
          status: "error",
          duration: 3000,
        });
        return;
      }

      // 2. shorts_contract에서 commission 값 가져오기
      const { data: contractData, error: contractError } = await supabase
        .from("shorts_contract")
        .select("commission")
        .eq("contractor", emailInput)
        .single();

      if (contractError || !contractData) {
        toast({
          title: "계약 정보를 찾을 수 없습니다.",
          status: "error",
          duration: 3000,
        });
        return;
      }

      // 3. 토큰 발급
      const userId = userData.id;
      const tokenRes = await fetch("https://plpl.rhoonart.co.kr/auth/token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      const tokenJson = await tokenRes.json();
      const access_token = tokenJson.data?.access_token;

      // 4. 외부 API PUT
      const body = {
        id: selected.id,
        isrc: selected.isrc,
        album: selected.name_album,
        musician: selected.name_musician,
        trackName: selected.name_track,
        labelId: selected.ownership_label,
        trackCodeId: selected.track_code,
        contractorId: userId,
        shareRatio: Number(selected.ownership_share) || 0,
        playlist: true,
        copyrights: true,
        laeeblyForShorts: true,
        legal: true,
        melon: 0,
        creatorId: userId,
        createAt: selected.created_date
          ? new Date(selected.created_date).toISOString()
          : new Date().toISOString(),
        updateAt: new Date().toISOString(),
      };

      await fetch(`https://plpl.rhoonart.co.kr/ownerships/${selected.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access_token}`,
        },
        body: JSON.stringify(body),
      });

      // 5. ownership 데이터 업데이트
      const { error: ownershipError } = await supabase
        .from("ownership")
        .update({ ownership_user: emailInput })
        .eq("id", selected.id);

      if (ownershipError) {
        throw ownershipError;
      }

      // 6. track 데이터의 commission_rate 업데이트
      const { error: trackError } = await supabase
        .from("track")
        .update({ commission_rate: contractData.commission })
        .eq("id", selected.track_code);

      if (trackError) {
        throw trackError;
      }

      toast({
        title: "지급이 완료되었습니다.",
        status: "success",
        duration: 3000,
      });

      setEmailModalOpen(false);
      setEmailInput("");
      fetchData();
    } catch (error) {
      toast({
        title: "지급 실패",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleRevoke = async () => {
    if (!selected) return;

    try {
      // 1. 기본 계정(jaeeui.kwon@rhoonart.com)의 user id 조회
      const { data: userData, error: userError } = await supabase
        .from("user")
        .select("id")
        .eq("email", "jaeeui.kwon@rhoonart.com")
        .single();

      if (userError || !userData) {
        toast({
          title: "기본 계정을 찾을 수 없습니다.",
          status: "error",
          duration: 3000,
        });
        return;
      }

      // 2. 토큰 발급
      const userId = userData.id;
      const tokenRes = await fetch("https://plpl.rhoonart.co.kr/auth/token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      const tokenJson = await tokenRes.json();
      const access_token = tokenJson.data?.access_token;

      // 3. 외부 API PUT
      const body = {
        id: selected.id,
        isrc: selected.isrc,
        album: selected.name_album,
        musician: selected.name_musician,
        trackName: selected.name_track,
        labelId: selected.ownership_label,
        trackCodeId: selected.track_code,
        contractorId: userId,
        shareRatio: Number(selected.ownership_share) || 0,
        playlist: true,
        copyrights: true,
        laeeblyForShorts: true,
        legal: true,
        melon: 0,
        creatorId: userId,
        createAt: selected.created_date
          ? new Date(selected.created_date).toISOString()
          : new Date().toISOString(),
        updateAt: new Date().toISOString(),
      };

      await fetch(`https://plpl.rhoonart.co.kr/ownerships/${selected.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access_token}`,
        },
        body: JSON.stringify(body),
      });

      // 4. ownership 데이터 업데이트
      const { error: ownershipError } = await supabase
        .from("ownership")
        .update({ ownership_user: "jaeeui.kwon@rhoonart.com" })
        .eq("id", selected.id);

      if (ownershipError) {
        throw ownershipError;
      }

      // 5. track 데이터의 commission_rate 업데이트
      const { error: trackError } = await supabase
        .from("track")
        .update({ commission_rate: 10 })
        .eq("id", selected.track_code);

      if (trackError) {
        throw trackError;
      }

      toast({
        title: "회수가 완료되었습니다.",
        status: "success",
        duration: 3000,
      });

      fetchData();
    } catch (error) {
      toast({
        title: "회수 실패",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handlePageInput = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) setInputPage(value);
  };

  const handlePageGo = () => {
    const num = parseInt(inputPage, 10);
    if (num >= 1 && num <= totalPages) setPage(num);
    else
      toast({ title: "올바른 페이지 번호를 입력하세요.", status: "warning" });
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Box maxW="1400px" mx="auto" py={8}>
      <Flex mb={4} gap={2} align="center">
        <Input
          placeholder="ISRC, 오너쉽 유저, 트랙코드, 트랙명, 앨범명 검색"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={handleKeyPress}
          width="370px"
        />
        <Button colorScheme="blue" onClick={handleSearch}>
          검색
        </Button>
      </Flex>
      <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="md">
        <Table size="sm">
          <Thead bg="gray.50">
            <Tr>
              {columns.map((col) => (
                <Th key={col.key}>{col.label}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center">
                  <Spinner />
                </Td>
              </Tr>
            ) : data.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center">
                  <Text color="gray.400">데이터 없음</Text>
                </Td>
              </Tr>
            ) : (
              data.map((row) => (
                <Tr key={row.id}>
                  {columns.map((col) => (
                    <Td
                      key={col.key}
                      maxW="200px"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {col.key === "actions" ? (
                        <Flex gap={2}>
                          <Button
                            size="sm"
                            colorScheme="green"
                            onClick={() => {
                              setSelected(row);
                              setEmailModalOpen(true);
                            }}
                          >
                            지급
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="red"
                            onClick={() => {
                              setSelected(row);
                              handleRevoke();
                            }}
                          >
                            회수
                          </Button>
                        </Flex>
                      ) : col.key === "created_date" ? (
                        row[col.key] ? (
                          new Date(row[col.key]).toLocaleDateString()
                        ) : (
                          "-"
                        )
                      ) : col.key === "commission_rate" ? (
                        row[col.key] ? (
                          `${row[col.key]}%`
                        ) : (
                          "-"
                        )
                      ) : col.key === "ownership_share" ? (
                        row[col.key] ? (
                          `${row[col.key]}%`
                        ) : (
                          "-"
                        )
                      ) : (
                        row[col.key] ?? "-"
                      )}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
      <Flex justify="center" mt={4} gap={2} align="center">
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          isDisabled={page === 1}
        >
          이전
        </Button>
        <Input
          value={inputPage}
          onChange={handlePageInput}
          onBlur={handlePageGo}
          size="sm"
          width="50px"
          textAlign="center"
          fontSize="sm"
          placeholder="페이지"
        />
        <Button size="sm" onClick={handlePageGo}>
          이동
        </Button>
        <Text fontSize="sm" px={2}>
          / {totalPages}
        </Text>
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          isDisabled={page === totalPages}
        >
          다음
        </Button>
      </Flex>

      {/* 이메일 입력 모달 */}
      <Modal isOpen={emailModalOpen} onClose={() => setEmailModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>이메일 입력</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="이메일 주소를 입력하세요"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleGrant}>
              지급하기
            </Button>
            <Button ml={3} onClick={() => setEmailModalOpen(false)}>
              취소
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ShortsAlbums;
